import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { http } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Navigate } from 'react-router-dom';
import ContentManager from '@/components/ContentManager';
import { ArrowLeft, BarChart3, MessageSquare, Vote, ClipboardList, Sparkles, Mail, Users, LifeBuoy, ShieldAlert, Stethoscope, Bot, Trash2, Ticket, HeartPulse } from 'lucide-react';

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (!user?.is_admin) return;
    http.get('/admin/stats').then(r => setStats(r.data));
    http.get('/admin/users').then(r => setUsers(r.data));
  }, [user]);

  if (!user?.is_admin) return <Navigate to="/app" replace />;
  if (!stats) return <div className="p-6 text-navy/60">Loading…</div>;

  const Card = ({ label, value }) => (
    <div className="bg-white rounded-3xl p-5 card-soft">
      <p className="text-xs uppercase tracking-widest text-navy/50">{label}</p>
      <p className="font-heading text-3xl mt-1">{value}</p>
    </div>
  );

  const NavTile = ({ to, icon: Icon, label, sub, testid }) => (
    <Link to={to} data-testid={testid}
      className="bg-navy text-white rounded-3xl p-6 card-soft hover:-translate-y-0.5 transition-smooth flex flex-col gap-3">
      <Icon size={20} className="text-gold" strokeWidth={1.5}/>
      <div>
        <p className="font-heading text-lg">{label}</p>
        <p className="text-xs text-white/60 mt-1">{sub}</p>
      </div>
    </Link>
  );

  return (
    <div className="p-6 lg:px-10 lg:pt-10 space-y-6" data-testid="admin-page">
      <Link to="/app" data-testid="admin-back" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy">
        <ArrowLeft size={14}/> Back to home
      </Link>
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Admin</p>
        <h1 className="font-heading text-3xl mt-1">MCCN Dashboard</h1>
        <p className="text-navy/50 text-sm mt-1">Anonymous aggregate only. No private journals, chats, or assessment answers.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        <NavTile to="/app/admin/analytics" icon={BarChart3} label="Full Analytics" sub="Engagement · Retention · Funnel · Heatmap · AI Insights" testid="admin-nav-analytics"/>
        <NavTile to="/app/admin/member-insights" icon={HeartPulse} label="Member Insights" sub="Wellness score trend · daily check-in rate · filter by date & member" testid="admin-nav-member-insights"/>
        <NavTile to="/app/admin/ai-usage" icon={Bot} label="AI Usage Insights" sub="Daily Coach messages per member · who's near the 50/day cap · A/B copy" testid="admin-nav-ai-usage"/>
        <NavTile to="/app/admin/beta-feedback" icon={MessageSquare} label="Beta Feedback" sub="Bug reports · feature requests · mark resolved" testid="admin-nav-feedback"/>
        <NavTile to="/app/admin/contact" icon={LifeBuoy} label="Support Inbox" sub="Contact-form messages · reply · mark done" testid="admin-nav-contact"/>
        <NavTile to="/app/admin/community" icon={ShieldAlert} label="Community Moderation" sub="Review reported posts & comments · remove · resolve" testid="admin-nav-community"/>
        <NavTile to="/app/admin/therapists" icon={Stethoscope} label="Therapist Directory" sub="Add & manage licensed providers" testid="admin-nav-therapists"/>
        <NavTile to="/app/admin/analytics?tab=surveys" icon={ClipboardList} label="Weekly Surveys" sub="NPS + qualitative signal" testid="admin-nav-surveys"/>
        <NavTile to="/app/admin/analytics?tab=features" icon={Vote} label="Roadmap Votes" sub="What users want most" testid="admin-nav-roadmap"/>
        <NavTile to="/app/admin/email-log" icon={Mail} label="Email Delivery Log" sub="Resend status · SPF/DKIM/DMARC · retry & refresh" testid="admin-nav-email-log"/>
        <NavTile to="/app/admin/users" icon={Users} label="User Management" sub="Reset data · delete accounts · audit-logged" testid="admin-nav-users"/>
        <NavTile to="/app/admin/deletions" icon={Trash2} label="Account Deletions" sub="Who deleted which account & when · admin + self-service" testid="admin-nav-deletions"/>
        <NavTile to="/app/admin/promo-codes" icon={Ticket} label="Promo Codes" sub="Create 100%-off comp/beta codes · track redemptions · disable" testid="admin-nav-promo"/>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Card label="Total users" value={stats.total_users} />
        <Card label="Subscribers" value={stats.premium_users} />
        <Card label="Free" value={stats.free_users} />
        <Card label="Assessments" value={stats.assessments_completed} />
        <Card label="Check-ins" value={stats.checkins_total} />
        <Card label="Lessons done" value={stats.lessons_completed} />
        <Card label="Event regs" value={stats.event_registrations} />
      </div>

      {stats.mood_distribution.length > 0 && (
        <div className="bg-white rounded-3xl p-6 card-soft">
          <p className="font-heading text-lg mb-3">Mood distribution (aggregate)</p>
          <div className="space-y-2">
            {stats.mood_distribution.map(m => (
              <div key={m.mood} className="flex items-center gap-3">
                <span className="text-sm w-24">{m.mood}</span>
                <div className="flex-1 h-2 bg-beige rounded-full overflow-hidden">
                  <div className="h-full bg-gold" style={{width: `${Math.min(100, (m.count/Math.max(1, stats.checkins_total))*100)}%`}}/>
                </div>
                <span className="text-xs text-navy/50 w-8 text-right">{m.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-3xl p-6 card-soft">
        <p className="font-heading text-lg mb-3">Users ({users.length})</p>
        <div className="divide-y divide-navy/5">
          {users.map(u => (
            <div key={u.id} className="py-3 flex items-center justify-between" data-testid={`admin-user-${u.email}`}>
              <div>
                <p className="text-sm font-medium">{u.name}</p>
                <p className="text-xs text-navy/50">{u.email}</p>
              </div>
              <div className="flex gap-2">
                {u.is_premium && <span className="text-[10px] bg-gold text-white px-2 py-0.5 rounded-full">Member</span>}
                {u.is_admin && <span className="text-[10px] bg-navy text-white px-2 py-0.5 rounded-full">Admin</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      <ContentManager />

      <p className="text-xs text-navy/40 text-center pt-2 inline-flex items-center gap-1 justify-center w-full">
        <Sparkles size={11} className="text-gold"/> More admin tools live in <Link to="/app/admin/analytics" className="underline">Full Analytics</Link>.
      </p>
    </div>
  );
}
